import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Lightbulb, AlertTriangle, Calculator, HelpCircle } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPage } from "@/lib/landingpages";
import ShelfCalculator from "@/components/calculators/ShelfCalculator";

function renderCalculator(kind) {
  if (kind === "shelf") return <ShelfCalculator />;
  return null;
}

export default function SeoLandingPage() {
  const { seoSlug } = useParams();
  const page = getPage(seoSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [seoSlug]);

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
        <Seo title="Seite nicht gefunden | RegalAbstand" description="Diese Seite wurde nicht gefunden." />
        <h1 className="text-2xl font-semibold">Seite nicht gefunden</h1>
        <p className="text-muted-foreground mt-2">Diese Seite gibt es nicht.</p>
        <Link to="/" className="text-primary underline mt-4 inline-block">Zur Startseite</Link>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/${page.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (page.faq || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: origin || undefined },
        { "@type": "ListItem", position: 2, name: page.group },
        { "@type": "ListItem", position: 3, name: page.h1, item: pageUrl },
      ],
    },
  ];
  if (!page.comingSoon) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: page.h1,
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: pageUrl,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: page.metaDescription,
    });
  }

  return (
    <div>
      <Seo title={page.metaTitle} description={page.metaDescription} canonical={pageUrl} />
      <JsonLd data={jsonLd} />

      {/* Hero + Tool above the fold */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.08), rgba(249,115,22,0.06), rgba(251,250,247,0))", height: "45%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-6 relative">
          <Badge variant="secondary" className="mb-3">{page.group}</Badge>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl">{page.h1}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{page.intro}</p>

          {!page.comingSoon ? (
            <div className="mt-6">{renderCalculator(page.calculator)}</div>
          ) : (
            <Card className="mt-6 p-6 border bg-secondary/40">
              <p className="font-heading font-semibold">Rechner in Vorbereitung</p>
              <p className="text-sm text-muted-foreground mt-1">
                Diese Funktion bauen wir gerade. Schau dir solange die verwandten Rechner unten an.
              </p>
            </Card>
          )}
        </div>
      </section>

      {!page.comingSoon && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-4"><AdSlot variant="banner" /></div>
      )}

      {/* Erklärung */}
      {!page.comingSoon && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-4 w-4 text-primary" />
              <h2 className="text-xl sm:text-2xl font-semibold">Welche Werte brauche ich?</h2>
            </div>
            <p className="text-muted-foreground mb-3">{page.forWhat}</p>
            <ul className="space-y-2">
              {(page.whichValues || []).map((it, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base">
                  <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-accent" />
              <h2 className="text-xl sm:text-2xl font-semibold">Wie wird gerechnet?</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">{page.howItWorks}</p>
          </div>
        </section>
      )}

      {/* Beispielrechnung */}
      {page.example && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
          <Card className="p-5 border" data-testid="seo-example">
            <h2 className="font-heading font-semibold mb-3">{page.example.title}</h2>
            <ol className="space-y-1.5">
              {page.example.lines.map((l, i) => (
                <li key={i} className="text-sm text-muted-foreground leading-relaxed">{l}</li>
              ))}
            </ol>
          </Card>
        </section>
      )}

      {/* Häufige Fehler */}
      {page.mistakes && page.mistakes.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fehler</h2>
          <div className="space-y-2.5">
            {page.mistakes.map((m, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm sm:text-base">
                <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{m}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Anzeige zwischen Inhalt und FAQ */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6"><AdSlot variant="banner" /></div>

      {/* FAQ */}
      {page.faq && page.faq.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-primary" />
            <h2 className="text-xl sm:text-2xl font-semibold">Häufige Fragen</h2>
          </div>
          <Faq items={page.faq} />
        </section>
      )}

      {/* Interne Links */}
      {page.related && page.related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-lg font-semibold mb-3">Weitere Rechner</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {page.related.map((rs) => {
              const rp = getPage(rs);
              if (!rp) return null;
              return (
                <Link key={rs} to={`/${rs}`} data-testid={`related-link-${rs}`}>
                  <Card className="p-3 border hover:shadow-md transition-shadow flex items-center justify-between gap-2 h-full">
                    <span className="text-sm font-medium">{rp.navLabel}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Anzeige am Seitenende */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12"><AdSlot variant="banner" /></div>
    </div>
  );
}
